import React from 'react'
import SmoothScroll from 'smoothscroll-polyfill'
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom'
import { withCookies } from 'react-cookie'
import Loadable from 'react-loadable'
import Splash from './splash'
import './../static/css/index.css'
import './../static/images/favicon.png'
import './../static/images/favicon512.png'
import Polyglot from 'node-polyglot'
import LocaleEl from './../i18n/locale-el'
import LocaleEn from './../i18n/locale-en'
import Config from './config'

const CompLoading = props => {
  if (props.isLoading) {
    if (props.timedOut) {
      return null
    } else if (props.pastDelay) {
      return <div id='loading' />
    } else {
      return null
    }
  } else if (props.error) {
    return null
  } else {
    return null
  }
}

const LoadableUser = Loadable({
  loader: () => import(/* webpackChunkName: "user" */ './user/user'),
  loading: CompLoading,
  timeout: 10000,
  delay: 500
})

const LoadableCertified = Loadable({
  loader: () =>
    import(/* webpackChunkName: "certified" */ './certified/certified'),
  loading: CompLoading,
  timeout: 10000,
  delay: 500
})

const LoadableStaff = Loadable({
  loader: () => import(/* webpackChunkName: "staff" */ './staff/staff'),
  loading: CompLoading,
  timeout: 10000,
  delay: 500
})

let BASE_URL = '/nlg-api'
if (process.env.NODE_ENV !== 'production' && Config.baseUrl) {
  BASE_URL = Config.baseUrl
}

class App extends React.Component {
  polyglot = {
    el: new Polyglot({ phrases: LocaleEl, locale: 'el' }),
    en: new Polyglot({ phrases: LocaleEn, locale: 'en' })
  }

  selectLang = () => {
    let ret = 'el'
    const navLang = (window.navigator.language || window.navigator.userLanguage
    ).substr(0, 2)
    if (Object.keys(this.polyglot).includes(this.props.cookies.get('lang'))) {
      ret = this.props.cookies.get('lang')
    } else if (Object.keys(this.polyglot).includes(navLang)) {
      ret = navLang
    }
    window.recaptchaOptions = {
      lang: ret
    }
    return ret
  }

  state = {
    lang: this.selectLang(),
    url: {
      config: BASE_URL + '/config-params',
      prefix: BASE_URL + '/country-codes',
      sso: 'https://sso.nlg.gr',
      newsletter: Config.newsletterUrl,
      privacyPolicy: '//register.nlg.gr/policy',
      staff: {
        basic: BASE_URL + '/staff/find-user',
        pin: BASE_URL + '/staff/validate-pin',
        resend: BASE_URL + '/staff/resend',
        account: BASE_URL + '/staff/receive-password'
      },
      certified: {
        taxis: BASE_URL + '/certified/tax-data',
        logout: BASE_URL + '/certified/oauth-logout',
        basic: BASE_URL + '/certified/receive-ssn',
        pin: BASE_URL + '/certified/receive-pin',
        resend: BASE_URL + '/certified/resend-pin',
        account: BASE_URL + '/certified/receive-password'
      },
      user: {
        basic: BASE_URL + '/user/new-user',
        pin: BASE_URL + '/user/validate-pin',
        resend: BASE_URL + '/user/resend',
        account: BASE_URL + '/user/receive-data'
      }
    },
    age: {
      min: 13,
      max: 150
    },
    captcha: {
      siteKey: '6Le91iQUAAAAAFFeKyoiCx_b9BSbuV9whACVjVmG'
    },
    password: {
      levenshteinLimit: 2,
      minLcs: 40,
      minNonAlpha: 2,
      minConsecutiveNums: 3,
      minLen: 6,
      minUniq: 5
    },
    pin: {
      len: 6
    },
    prefix: []
  }

  constructor (props) {
    super(props)
    SmoothScroll.polyfill()
    document.title = this.polyglot[this.state.lang].t('title')
  }

  componentDidUpdate = (prevProps, prevState) => {
    if (prevState.lang !== this.state.lang) {
      document.title = this.polyglot[this.state.lang].t('title')
    }
  }

  componentDidMount = () => {
    window
      .fetch(this.state.url.config)
      .then(r => r.json())
      .then(r => {
        if (!r.error) {
          this.setState({
            age: {
              min: r.userMinAge,
              max: r.userMaxAge
            },
            password: {
              levenshteinLimit: r.levenshteinLimit,
              minLcs: r.minLcs,
              minNonAlpha: r.minNonAlpha,
              minConsecutiveNums: r.minConsecutiveNumbers,
              minLen: r.minLen,
              minUniq: r.minUniq
            },
            pin: {
              len: r.pinLen
            }
          })
        }
      })

    window
      .fetch(this.state.url.prefix)
      .then(r => r.json())
      .then(r => {
        if (!r.error) {
          this.setState({
            prefix: r.countryCodes
          })
        }
      })
  }

  changeLang = lang => {
    window.recaptchaOptions.lang = lang
    this.setState({ lang })
  }

  render = () => (
    <Router>
      <Switch>
        <Route
          exact
          path='/error'
          render={props => (
            <Splash
              {...props}
              polyglot={this.polyglot[this.state.lang]}
              changeLang={this.changeLang}
              error
            />
          )}
        />
        <Route
          exact
          path='/staff'
          render={props => (
            <LoadableStaff
              {...props}
              polyglot={this.polyglot[this.state.lang]}
              config={this.state}
              changeLang={this.changeLang}
            />
          )}
        />
        <Route
          exact
          path='/certified/error/:tin?'
          render={props => (
            <LoadableCertified
              {...props}
              polyglot={this.polyglot[this.state.lang]}
              config={this.state}
              changeLang={this.changeLang}
              error
            />
          )}
        />
        <Route
          exact
          path='/certified/:token?'
          render={props => (
            <LoadableCertified
              {...props}
              polyglot={this.polyglot[this.state.lang]}
              config={this.state}
              changeLang={this.changeLang}
              error={false}
            />
          )}
        />
        <Route
          exact
          path='/user'
          render={props => (
            <LoadableUser
              {...props}
              polyglot={this.polyglot[this.state.lang]}
              config={this.state}
              changeLang={this.changeLang}
            />
          )}
        />
        <Route
          exact
          render={props => (
            <Splash
              {...props}
              config={this.state}
              polyglot={this.polyglot[this.state.lang]}
              changeLang={this.changeLang}
            />
          )}
        />
      </Switch>
    </Router>
  )
}

export default withCookies(App)
